import {
  simplifiedSignupDefaultValues,
  simplifiedSignupSchema,
} from '@/components/common/UpdateProfile/Schemas';
import { showToast } from '@/components/ui/sonner';
import { UPDATE_USER } from '@/Graphql/mutation';
import { GET_CURRENT_USER } from '@/Graphql/queries';
import useLogout from '@/hooks/useLogout';
import { handleError } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useLazyQuery, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import SimplifiedSignup from './components/SimplifiedSignup';

export type SimplifiedSignupSchemaType = z.infer<typeof simplifiedSignupSchema>;

const SignUp = () => {
  const navigate = useNavigate();
  const { userDetails } = authState();
  const methods = useForm<SimplifiedSignupSchemaType>({
    resolver: zodResolver(simplifiedSignupSchema),
    defaultValues: simplifiedSignupDefaultValues,
  });
  const { handleSubmit } = methods;

  const [loading, setLoading] = useState(false);

  const [getCurrentUser] = useLazyQuery(GET_CURRENT_USER, {
    onCompleted: async (res) => {
      const user = res?.getCurrentUser;
      authState({
        authenticated: true,
        loading: false,
        userDetails: user,
      });
      // Always navigate to home after simplified signup
      navigate(getRoutes.home);
    },
    fetchPolicy: 'no-cache',
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      await getCurrentUser();
      showToast({ variant: 'success', message: 'Account created successfully' });
      setLoading(false);
    },
    onError: (error) => {
      handleError(error);
      setLoading(false);
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      updateUser({
        variables: {
          data: {
            _id: userDetails?._id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: Role.Trainee,
            traineeAgeConsent: data.ageConfirmation,
            ...(data.referralCode && { referralCode: data.referralCode }),
          },
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Signup failed:', err);
      setLoading(false);
    }
  });
  const { logout } = useLogout();
  const handleNavigateLogin = () => {
    logout();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className='w-full flex flex-col items-center mt-8'>
          <div className='w-[95%] max-w-[640px] flex flex-col items-center gap-6'>
            <div className='flex flex-col gap-2 '>
              <h2 className='text-center'>Create Account</h2>
              <h4 className='font-normal text-[#A8ADB9] text-center'>
                It seems you are new to FitStatic. Please choose the type of <br /> account you want to create at FitStatic.<br />
              </h4>
            </div>
            <SimplifiedSignup
              handleNavigateLogin={handleNavigateLogin}
              loading={loading}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default SignUp;
